/**
 * RNG — stateless, seeded, and addressable by tick.
 *
 * There is no generator object and no cursor to advance, because a cursor is a
 * second clock: two callers drawing in a different order would diverge, and an
 * offline settle would stop being bit-identical to a live run.
 *
 * Instead every draw is a pure hash of (seed, tickIndex, channel). Ask for tick
 * 12,345 on channel OUTCOME and you get the same number forever, whether you
 * arrived there one tick at a time with the tab focused or in a single settle
 * after two days away.
 *
 * All arithmetic is 32-bit integer (Math.imul, >>>), so results are identical on
 * every JS engine. No Math.random. No floats are stored anywhere in the state.
 */

/** Draw channels. A channel keeps two independent decisions on one tick uncorrelated. */
export const CHANNEL = {
  OUTCOME: 1,
  WEIGHT: 2,
  SPECIAL_PICK: 3,
  FLAVOUR: 4,
  /**
   * WHICH FORM OF THE QUARRY CAME UP — see variants.ts. Its own channel and not
   * a second read of WEIGHT, because the two decisions must be uncorrelated: a
   * shared address would tie "is this an Elder" to "is this a big one" on every
   * tick, so the rarest form would always land on the same slice of the weight
   * band and the record it produced would be a property of the hash rather than
   * of the fish.
   */
  VARIANT: 5,
} as const;

export type Channel = (typeof CHANNEL)[keyof typeof CHANNEL];

/** murmur3 32-bit finalizer. Avalanches a single word. */
function mix32(x: number): number {
  let h = x | 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/**
 * Uniform uint32 for one (seed, tick, channel) address.
 *
 * `tick` may exceed 2^31, so its high word is folded in separately rather than
 * being silently truncated by `| 0` — otherwise ticks 2^32 apart would collide
 * and a very long-lived save would start repeating its own history.
 */
export function hash32(seed: number, tick: number, channel: number): number {
  const lo = tick | 0;
  const hi = Math.floor(tick / 4294967296) | 0;
  let h = seed | 0;
  h = Math.imul(h ^ lo, 0x9e3779b1) | 0;
  h = Math.imul(h ^ hi, 0x27d4eb2f) | 0;
  h = (h ^ Math.imul(channel | 0, 0x85ebca77)) | 0;
  return mix32(h);
}

/**
 * Uniform float in [0, 1). Built from the top 24 bits so the result is always a
 * dyadic rational exactly representable in a double: identical bits everywhere.
 * Nothing derived from this is ever accumulated into the save.
 */
export function rand01(seed: number, tick: number, channel: number): number {
  return (hash32(seed, tick, channel) >>> 8) / 16777216;
}

/**
 * Uniform integer in [0, n). Modulo bias is at most 1 part in 2^32/n and is
 * accepted deliberately: rejection sampling would need a loop, and a loop needs
 * a cursor, and a cursor is the thing this module exists to avoid.
 */
export function randBelow(seed: number, tick: number, channel: number, n: number): number {
  if (!Number.isInteger(n) || n <= 0) return 0;
  return hash32(seed, tick, channel) % n;
}

/** Uniform integer in [lo, hi], inclusive both ends. */
export function randRange(
  seed: number,
  tick: number,
  channel: number,
  lo: number,
  hi: number,
): number {
  if (hi <= lo) return lo;
  return lo + randBelow(seed, tick, channel, hi - lo + 1);
}
